// The visit document (D-87): built from the draft, one revision at a time,
// at most one pending outbox item per visit.
//
// Every change to a started draft writes the next revision over the queued
// item; while the item is in flight the draft is marked dirty on the item
// and the engine re-enqueues it when the cycle ends. Values travel in the
// API's shape (decimal strings, ok/fail, option codes, ISO dates) and only
// when they are real values: a rejected number or an empty text is absent.
import type { VisitSyncItem, ValueIn, ObservationIn, PhotoIn } from '../api/types';
import {
  registerDocumentBuilder,
  type FieldcoreDb,
  type OutboxRecord,
  type VisitDraftRecord,
} from '../db/database';
import { parseFormDocument, type FormDocument, type FormField } from '../form/document';
import { formatNumeric } from '../form/numeric';
import type { FieldValue } from '../form/values';

/** The API text of one value, or null when the field holds no real value. */
export function apiValueOf(
  field: FormField | undefined,
  value: FieldValue | undefined,
): string | null {
  if (value === undefined) return null;
  switch (value.kind) {
    case 'numeric':
      if (value.state !== 'ok' || value.value === null) return null;
      return formatNumeric(value.value, field?.decimals ?? null);
    case 'text':
      return value.value.trim() === '' ? null : value.value;
    case 'check':
      return value.value;
    case 'select':
      return value.value;
    case 'date':
      return value.value;
    case 'photo':
      // A photo field carries no value: the declared photos count for it.
      return null;
  }
}

function fieldsOf(document: FormDocument): Map<string, { field: FormField; groupId: string }> {
  const index = new Map<string, { field: FormField; groupId: string }>();
  for (const group of document.groups) {
    for (const field of group.fields)
      index.set(field.field_definition_id, { field, groupId: group.id });
  }
  return index;
}

function safeDocument(raw: unknown): FormDocument | null {
  try {
    return parseFormDocument(raw);
  } catch {
    return null;
  }
}

/** The document of a draft as the API takes it. Pure over the draft. */
export function documentOf(draft: VisitDraftRecord): VisitSyncItem {
  const form = safeDocument(draft.document);
  const index =
    form === null ? new Map<string, { field: FormField; groupId: string }>() : fieldsOf(form);
  const values: ValueIn[] = [];
  for (const [fieldId, value] of Object.entries(draft.values)) {
    const entry = index.get(fieldId);
    if (entry === undefined) continue;
    const text = apiValueOf(entry.field, value);
    if (text === null) continue;
    values.push({
      field_definition_id: fieldId,
      template_group_id: entry.groupId,
      value: text,
      // INC-14a-1 (B-14): the instant the person wrote this value, never the
      // draft's last edit, which another value moves.
      recorded_at: draft.value_times?.[fieldId] ?? new Date(draft.updated_at).toISOString(),
      geo: null,
    });
  }
  const observations: ObservationIn[] = draft.observations
    .filter((observation) => observation.text.trim() !== '')
    .map((observation) => ({
      client_generated_id: observation.client_generated_id,
      scope: observation.scope,
      field_definition_id: observation.field_definition_id,
      template_group_id: observation.template_group_id,
      text: observation.text,
      recorded_at: observation.recorded_at,
      geo: observation.geo,
    }));
  const photos: PhotoIn[] = draft.photos.map((photo) => ({
    client_generated_id: photo.client_generated_id,
    template_group_id: photo.template_group_id,
    field_definition_id: photo.field_definition_id,
    observation_client_generated_id: photo.observation_client_generated_id,
    captured_at: photo.captured_at,
    checksum: photo.checksum,
    bytes: photo.bytes,
    geo: photo.geo,
  }));
  // INC-06b-2b (D-113): a planned visit names its slot and carries no reason;
  // an unplanned one carries the reason and names no slot. A planned draft
  // whose slot is gone was converted before it got here (D-160).
  const planned = draft.visit_kind === 'planned' && draft.visit_slot_id !== null;
  const item: VisitSyncItem = {
    client_generated_id: draft.client_generated_id,
    client_revision: draft.client_revision,
    version: draft.version ?? 0,
    site_id: draft.site_id,
    started_at: draft.started_at ?? new Date(draft.created_at).toISOString(),
    visit_kind: planned ? 'planned' : 'unplanned',
    ...(planned
      ? { visit_slot_id: draft.visit_slot_id }
      : { unplanned_reason: draft.unplanned_reason ?? '' }),
    template_version_id: draft.template_version_id,
    form_content_hash: draft.form_content_hash,
    geo_start: draft.geo_start,
    // INC-06b-2b (D-110, D-152): the crew's companions, the slot's as the
    // initial value and the operator's edits after it. The lead is the
    // token's user and never travels; so does the author (D-187), which is
    // local and adds no member to the body (criterion 69).
    companions: draft.companions,
    values,
    observations,
    photos,
    close: null,
  };
  if (draft.status === 'closing' && draft.ended_at !== null) {
    item.close = { ended_at: draft.ended_at, geo_end: draft.geo_end };
  }
  return item;
}

registerDocumentBuilder(documentOf);

function newItem(draft: VisitDraftRecord, now: number): OutboxRecord {
  return {
    id: crypto.randomUUID(),
    kind: 'visit_document',
    client_generated_id: draft.client_generated_id,
    payload: documentOf(draft),
    client_revision: draft.client_revision,
    dirty: 0,
    status: 'queued',
    enqueued_at: now,
    attempts: 0,
    next_attempt_at: null,
    last_code: null,
    last_http_status: null,
    // INC-06b-2b (D-187): the item is its draft's author's, and travels only
    // with that operator's session.
    author_id: draft.author_id,
    updated_at: now,
  };
}

/** Bump the draft's revision and queue it: the queued item is rewritten,
 *  an item in flight is marked dirty, a visit without a pending item gets
 *  one. Must run inside a transaction over visit_drafts and outbox_data;
 *  the draft passed is the already saved state. Returns the pending item. */
export async function enqueueRevision(
  database: FieldcoreDb,
  draft: VisitDraftRecord,
  now: number = Date.now(),
): Promise<OutboxRecord> {
  const next: VisitDraftRecord = {
    ...draft,
    client_revision: draft.client_revision + 1,
    updated_at: now,
  };
  await database.visit_drafts.put(next);
  const pending = await database.outbox_data
    .where('client_generated_id')
    .equals(draft.client_generated_id)
    .filter((item) => item.status === 'queued' || item.status === 'sending')
    .first();
  if (pending === undefined) {
    const item = newItem(next, now);
    await database.outbox_data.add(item);
    return item;
  }
  if (pending.status === 'sending') {
    const marked: OutboxRecord = { ...pending, dirty: 1, updated_at: now };
    await database.outbox_data.put(marked);
    return marked;
  }
  const rewritten: OutboxRecord = {
    ...pending,
    payload: documentOf(next),
    client_revision: next.client_revision,
    dirty: 0,
    updated_at: now,
  };
  await database.outbox_data.put(rewritten);
  return rewritten;
}

/** After a cycle: every item that was marked dirty while in flight yields
 *  the next document from the draft as it is now, with the version the
 *  answer left on it. A frozen draft yields nothing. Returns how many were queued.
 *
 *  INC-06b-2b (D-187): only the given author's items, when one is given. A
 *  cycle of one operator never rewrites another operator's queued document. */
export async function requeueDirty(
  database: FieldcoreDb,
  now: number = Date.now(),
  authorId: string | null = null,
): Promise<number> {
  return database.transaction('rw', [database.visit_drafts, database.outbox_data], async () => {
    const dirty = (await database.outbox_data.where('dirty').equals(1).toArray()).filter(
      (item) => authorId === null || item.author_id === authorId,
    );
    let queued = 0;
    for (const item of dirty) {
      await database.outbox_data.put({ ...item, dirty: 0, updated_at: now });
      const draft = await database.visit_drafts.get(item.client_generated_id);
      if (draft === undefined || draft.status === 'frozen') continue;
      if (item.status === 'queued') {
        // The cycle failed and the item went back to the queue: it carries
        // the latest document from now on.
        await database.outbox_data.put({
          ...item,
          payload: documentOf(draft),
          client_revision: draft.client_revision,
          dirty: 0,
          updated_at: now,
        });
      } else {
        await database.outbox_data.add(newItem(draft, now));
      }
      queued += 1;
    }
    return queued;
  });
}
